import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { STATUSES } from '../../constants/doctorProfile';
import InterestPicker from '../InterestPicker';
import api from '../../utils/authUtils';
import { mediaUrl } from '../../utils/mediaUrl';
import { downscalePhoto } from '../../utils/profilePhoto';

const BLUE = '#47ABDB';

const Form = styled.form`display:flex;flex-direction:column;gap:1rem;`;
const FormGroup = styled.div`display:flex;flex-direction:column;`;
const Label = styled.label`margin-bottom:3px;font-weight:bold;font-size:.9rem;color:#334155;`;
const Input = styled.input`padding:8px;border:1px solid #ccc;font-size:.9rem;&:disabled{background-color:#f3f4f6;color:#6b7280;}`;
const Select = styled.select`padding:8px;border:1px solid #ccc;font-size:.9rem;`;
const Textarea = styled.textarea`padding:8px;border:1px solid #ccc;font-size:.9rem;font-family:inherit;min-height:5.5rem;`;
const ButtonRow = styled.div`display:flex;gap:.75rem;align-items:center;`;
const SubmitButton = styled.button`background-color:${BLUE};color:#fff;border:none;padding:10px 20px;font-size:1rem;cursor:pointer;&:hover{background-color:#0056b3;}&:disabled{opacity:.6;cursor:not-allowed;}`;
const CancelButton = styled.button`background:none;border:1px solid #cbd5e1;color:#334155;padding:10px 20px;font-size:1rem;cursor:pointer;&:hover{background:#f1f5f9;}`;
const SectionTitle = styled.h3`margin:.5rem 0 0;font-size:1rem;font-weight:700;color:#1f2937;`;
const Checks = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.4rem;@media(max-width:480px){grid-template-columns:1fr;}`;
const CheckLabel = styled.label`display:flex;gap:.4rem;align-items:flex-start;font-weight:normal;font-size:.85rem;`;
const Message = styled.p`font-size:.875rem;color:${p => (p.error ? '#b91c1c' : '#065f46')};`;
const PhotoPreview = styled.img`margin-top:.6rem;width:96px;height:96px;border-radius:50%;object-fit:cover;border:2px solid ${BLUE};background:#eef2f7;`;
const PhotoActions = styled.div`display:flex;gap:.6rem;align-items:center;margin-top:.6rem;flex-wrap:wrap;`;
const PhotoButton = styled.button`background:#eef2f7;color:#111D4E;border:1px solid #cbd5e1;padding:6px 12px;font-size:.85rem;font-weight:600;cursor:pointer;&:hover:not(:disabled){background:#e2e8f0;}&:disabled{opacity:.6;cursor:not-allowed;}`;
const PhotoRemove = styled.button`background:none;border:none;color:#b91c1c;text-decoration:underline;cursor:pointer;font-size:.82rem;padding:0;&:disabled{opacity:.6;cursor:not-allowed;}`;
const PhotoHint = styled.div`font-size:.75rem;color:#64748b;margin-top:.35rem;`;
const PhotoError = styled.p`font-size:.8rem;color:#b91c1c;margin:.4rem 0 0;`;
const UpdatedBadge = styled.span`font-size:.68rem;font-weight:700;color:#166534;background:#dcfce7;padding:.1rem .4rem;margin-left:.4rem;text-transform:uppercase;letter-spacing:.02em;`;
const PreviousValueHint = styled.div`font-size:.78rem;color:#64748b;margin-top:.3rem;`;
const RevertLink = styled.button`background:none;border:none;padding:0;margin-left:.4rem;color:${BLUE};text-decoration:underline;font-size:.78rem;cursor:pointer;`;

// Shared by ProfileEditModal.js (the "Edit my profile" popup) and
// SpeakerInviteConfirmation.js (the first-time speaker onboarding page) - one
// set of fields, own component, so the two call sites can't drift apart.
export const editableFields = [
  ['title', 'Title'], ['firstName', 'First Name'], ['lastName', 'Last Name'],
  ['institution', 'Institution / practice / clinic'], ['specialty', 'Specialty'],
  ['streetAddress', 'Street Address'], ['city', 'City'], ['state', 'State/Province'],
  ['postalCode', 'Postal Code'],
];

const ProfileFormFields = ({
  draft, countryOptions, message, saving, t, roleLabel,
  onChange, onToggleList, onInterestsChange, onSubmit, onCancel,
  submitLabel, cancelLabel, fieldDiffs,
}) => {
  // Optional (see SpeakerInviteConfirmation.js): a field that changed shows
  // a small "updated" marker, and - only when there was a genuine previous
  // value, not just a gap being filled in - a one-click way to revert it,
  // right where the field already is instead of a separate decision screen.
  // ProfileEditModal.js's normal edit flow never passes this, so it's a
  // no-op there.
  const diffFor = (name) => (fieldDiffs || []).find((d) => d.field === name);
  const badgeFor = (name) => diffFor(name) && <UpdatedBadge>{t('profile.fieldUpdated', 'Updated')}</UpdatedBadge>;
  const fileRef = useRef(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState(null);

  // Picking a photo: downscale in the browser (works on smartphones - see
  // utils/profilePhoto.js), upload the small JPEG, and put its URL into the
  // same photoUrl field the rest of the form saves. Uploading only stores a
  // file - the account changes when the form is saved, so cancelling the
  // edit leaves the profile untouched.
  const handlePhotoPicked = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setPhotoBusy(true);
    setPhotoError(null);
    try {
      const ready = await downscalePhoto(file);
      const form = new FormData();
      form.append('file', ready);
      const { data } = await api.post('/api/attendee/profile/photo', form);
      onChange({ target: { name: 'photoUrl', value: data.photoUrl } });
    } catch (err) {
      console.error('Profile photo upload failed:', err);
      setPhotoError(err.response?.data || t('profile.photo.error', 'Could not upload the photo. Please try another image.'));
    } finally {
      setPhotoBusy(false);
    }
  };
  const hintFor = (name) => {
    const d = diffFor(name);
    if (!d?.currentValue) return null;
    return (
      <PreviousValueHint>
        {t('profile.previousValue', 'Previous value: {value}', { value: d.currentValue })}
        <RevertLink type="button" onClick={() => onChange({ target: { name, value: d.currentValue } })}>
          {t('profile.usePreviousValue', 'Use previous value')}
        </RevertLink>
      </PreviousValueHint>
    );
  };
  return (
  <Form onSubmit={onSubmit}>
    <FormGroup>
      <Label>{t('form.email', 'Email Address')}</Label>
      <Input value={draft.email || ''} disabled />
    </FormGroup>
    {editableFields.map(([name, label]) => (
      <FormGroup key={name}>
        <Label htmlFor={name}>{t(`form.${name}`, label)}{badgeFor(name)}</Label>
        <Input id={name} name={name} value={draft[name] || ''} onChange={onChange} />
        {hintFor(name)}
      </FormGroup>
    ))}
    <FormGroup>
      <Label htmlFor="country">{t('form.country', 'Country')}{badgeFor('country')}</Label>
      <Select id="country" name="country" value={draft.country || ''} onChange={onChange}>
        <option value="">{t('form.select', 'Select...')}</option>
        {/* Older profiles may hold a raw legacy country string that predates
            this picker (e.g. from the original public form) - keep it
            selectable as-is instead of silently blanking the field. */}
        {draft.country && !countryOptions.some((c) => c.name === draft.country) && (
          <option value={draft.country}>{draft.country}</option>
        )}
        {countryOptions.map((c) => (
          <option key={c.code} value={c.name}>{c.name}</option>
        ))}
      </Select>
      {hintFor('country')}
    </FormGroup>

    <FormGroup>
      <Label htmlFor="photoFile">{t('profile.photo.label', 'Profile photo')}{badgeFor('photoUrl')}</Label>
      {draft.photoUrl && (
        <PhotoPreview src={mediaUrl(draft.photoUrl)} alt="" referrerPolicy="no-referrer" onError={(e) => { e.target.style.visibility = 'hidden'; }} onLoad={(e) => { e.target.style.visibility = 'visible'; }} />
      )}
      <PhotoActions>
        <PhotoButton type="button" disabled={photoBusy || saving} onClick={() => fileRef.current && fileRef.current.click()}>
          {photoBusy ? t('profile.photo.uploading', 'Uploading…') : t('profile.photo.choose', 'Choose photo')}
        </PhotoButton>
        {draft.photoUrl && (
          <PhotoRemove type="button" disabled={photoBusy || saving} onClick={() => onChange({ target: { name: 'photoUrl', value: '' } })}>
            {t('profile.photo.remove', 'Remove photo')}
          </PhotoRemove>
        )}
        <input ref={fileRef} id="photoFile" type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoPicked} />
      </PhotoActions>
      {photoError && <PhotoError>{photoError}</PhotoError>}
      <PhotoHint>{t('profile.photo.hint', 'JPG, PNG or WebP - we resize your photo automatically.')}</PhotoHint>
      {hintFor('photoUrl')}
    </FormGroup>

    <SectionTitle>{t('speaker.onboarding.roles', 'Roles / status')}</SectionTitle>
    <Checks>
      {STATUSES.map((status) => (
        <CheckLabel key={status}>
          <input type="checkbox" checked={(draft.professionalStatuses || []).includes(status)} onChange={() => onToggleList('professionalStatuses', status)} />
          {roleLabel(status)}
        </CheckLabel>
      ))}
    </Checks>

    <SectionTitle>{t('speaker.onboarding.interests', 'Cardiac interests (choose at least 3)')}</SectionTitle>
    <InterestPicker value={draft.interests} onChange={onInterestsChange} />

    <SectionTitle>{t('signup.optionalDetails', 'Optional details')}</SectionTitle>
    <FormGroup>
      <Label htmlFor="shortBioEn">{t('speaker.field.shortBioEn', 'Short bio (English)')}{badgeFor('shortBioEn')}</Label>
      <Textarea id="shortBioEn" name="shortBioEn" value={draft.shortBioEn || ''} onChange={onChange} />
      {hintFor('shortBioEn')}
    </FormGroup>
    <FormGroup>
      <Label htmlFor="shortBioDe">{t('speaker.field.shortBioDe', 'Short bio (German)')}{badgeFor('shortBioDe')}</Label>
      <Textarea id="shortBioDe" name="shortBioDe" value={draft.shortBioDe || ''} onChange={onChange} />
      {hintFor('shortBioDe')}
    </FormGroup>
    <FormGroup>
      <Label htmlFor="motivationEn">{t('speaker.field.motivationEn', 'I am part of HEARTS - DEMOCRATIZING SCIENCE - because (English)')}{badgeFor('motivationEn')}</Label>
      <Textarea id="motivationEn" name="motivationEn" value={draft.motivationEn || ''} onChange={onChange} />
      {hintFor('motivationEn')}
    </FormGroup>
    <FormGroup>
      <Label htmlFor="motivationDe">{t('speaker.field.motivationDe', 'I am part of HEARTS - DEMOCRATIZING SCIENCE - because (German)')}{badgeFor('motivationDe')}</Label>
      <Textarea id="motivationDe" name="motivationDe" value={draft.motivationDe || ''} onChange={onChange} />
      {hintFor('motivationDe')}
    </FormGroup>
    <FormGroup>
      <Label htmlFor="linkedInUrl">{t('speaker.field.linkedin', 'LinkedIn')}{badgeFor('linkedInUrl')}</Label>
      <Input type="url" id="linkedInUrl" name="linkedInUrl" value={draft.linkedInUrl || ''} onChange={onChange} />
      {hintFor('linkedInUrl')}
    </FormGroup>
    <FormGroup>
      <Label htmlFor="websiteUrl">{t('speaker.field.website', 'Website')}{badgeFor('websiteUrl')}</Label>
      <Input type="url" id="websiteUrl" name="websiteUrl" value={draft.websiteUrl || ''} onChange={onChange} />
      {hintFor('websiteUrl')}
    </FormGroup>
    <FormGroup>
      <Label htmlFor="facebookUrl">{t('speaker.field.facebook', 'Facebook / Meta')}{badgeFor('facebookUrl')}</Label>
      <Input type="url" id="facebookUrl" name="facebookUrl" value={draft.facebookUrl || ''} onChange={onChange} />
      {hintFor('facebookUrl')}
    </FormGroup>
    <FormGroup>
      <Label htmlFor="instagramUrl">{t('speaker.field.instagram', 'Instagram')}{badgeFor('instagramUrl')}</Label>
      <Input type="url" id="instagramUrl" name="instagramUrl" value={draft.instagramUrl || ''} onChange={onChange} />
      {hintFor('instagramUrl')}
    </FormGroup>

    {message && <Message error={message.error}>{message.text}</Message>}
    <ButtonRow>
      <SubmitButton type="submit" disabled={saving}>
        {saving ? t('profile.saving', 'Saving…') : (submitLabel || t('profile.save', 'Save changes'))}
      </SubmitButton>
      {onCancel && (
        <CancelButton type="button" onClick={onCancel}>
          {cancelLabel || t('speaker.hub.cancel', 'Cancel')}
        </CancelButton>
      )}
    </ButtonRow>
  </Form>
  );
};

export default ProfileFormFields;
